import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import {
  dashboardRequest,
  isDashboardDataProviderEnabled,
} from "../providers/aws/client";

type Activity = {
  id: string;
  event: string;
  occurred_at: string;
  echo_id?: string;
  view?: string;
  outbound_id?: string;
  attribution?: string;
  classification?: string;
  identity_verified?: boolean;
};
const labels: Record<string, string> = {
  link_requested: "Portal link requested",
  otp_requested: "WhatsApp sign-in code requested",
  login_completed: "Signed in with WhatsApp",
  collection_viewed: "Viewed Echo collection",
  playback_requested: "Requested a video link",
  playback_started: "Started watching an Echo",
  playback_completed: "Finished watching an Echo",
  playback_failed: "Video could not play",
  create_clicked: "Opened Create",
  logout: "Signed out",
};

export function ContactPortalActivity({
  contactId,
}: {
  contactId: string | number;
}) {
  const [notice, setNotice] = useState("");
  const [copying, setCopying] = useState(false);
  const enabled = isDashboardDataProviderEnabled();
  const path = `/contacts/${encodeURIComponent(String(contactId))}`;
  const query = useQuery({
    queryKey: ["portal-activity", contactId],
    enabled,
    queryFn: () =>
      dashboardRequest<{ data: Activity[]; total: number }>(
        path + "/portal-activity",
      ),
    refetchInterval: 15000,
  });
  if (!enabled) return null;
  async function copyLink() {
    setCopying(true);
    setNotice("");
    try {
      const result = await dashboardRequest<{ url: string; tracked: boolean }>(
        path + "/portal-link",
        { method: "POST", body: "{}" },
      );
      await navigator.clipboard.writeText(result.url);
      setNotice(
        result.tracked
          ? "Tracked portal link copied."
          : "Portal link copied. Journey tracking is currently disabled.",
      );
    } catch {
      setNotice("Could not copy the portal link. Try again.");
    } finally {
      setCopying(false);
    }
  }
  return (
    <section
      className="rounded-lg border p-4 mb-4"
      aria-label="Portal activity"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="font-semibold">Portal activity</h3>
        <Button
          variant="outline"
          size="sm"
          onClick={copyLink}
          disabled={copying}
        >
          {copying ? "Copying…" : "Copy tracked portal link"}
        </Button>
      </div>
      <p className="text-xs text-muted-foreground mt-2">
        Link requests can be previews or shared links. Recipient attribution is
        confirmed only after a matching WhatsApp sign-in. Video link requests do
        not prove playback.
      </p>
      {notice && (
        <p role="status" className="text-sm mt-2">
          {notice}
        </p>
      )}
      {query.isPending && (
        <p className="text-sm mt-3">Loading portal activity…</p>
      )}
      {query.isError && (
        <p role="alert" className="text-sm mt-3">
          Portal activity could not load.{" "}
          <button className="underline" onClick={() => void query.refetch()}>
            Retry
          </button>
        </p>
      )}
      {query.data &&
        (query.data.data.length ? (
          <ol className="space-y-3 mt-4 max-h-72 overflow-auto">
            {query.data.data.map((activity) => (
              <li key={activity.id} className="text-sm">
                <div>
                  {labels[activity.event] ?? "Portal activity"}{" "}
                  {activity.echo_id && (
                    <span className="text-muted-foreground">
                      · Echo {activity.echo_id}
                    </span>
                  )}
                </div>
                <div className="text-xs text-muted-foreground">
                  <time dateTime={activity.occurred_at}>
                    {new Date(activity.occurred_at).toLocaleString()}
                  </time>
                  {activity.view && ` · ${activity.view}`}
                  {activity.classification === "preview" &&
                    " · automated preview"}
                  {activity.classification === "unverified" &&
                    " · unverified visitor"}
                  {activity.attribution === "verified_recipient" &&
                    " · verified message recipient"}
                  {activity.attribution === "shared_link" && " · shared link"}
                  {activity.outbound_id && ` · ${activity.outbound_id}`}
                </div>
              </li>
            ))}
          </ol>
        ) : (
          <p className="text-sm text-muted-foreground mt-3">
            No recorded portal activity yet. Earlier plain links cannot be
            tracked retroactively.
          </p>
        ))}
    </section>
  );
}
